import { useEffect, useState } from 'react';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../../firebase-client';
import TextInput from './formComponents/TextInput';
import SubmitButton from './formComponents/SubmitButton';
import ErrorMessage from './formComponents/ErrorMessage';

export default function ParentEmergencySyncDetailsModal({
  userId,
  onClose,
  onSaved,
}) {
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const loadDetails = async () => {
      try {
        const privateSnap = await getDoc(doc(db, 'privateUsers', userId));
        if (!privateSnap.exists() || cancelled) return;
        const privateData = privateSnap.data() || {};
        setPhone(String(privateData?.phone || ''));
        setAddress(String(privateData?.address || ''));
      } catch (err) {
        if (!cancelled) {
          setError('Could not load your profile details.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadDetails();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  const handleSave = async (event) => {
    event.preventDefault();
    setError('');

    const trimmedPhone = phone.trim();
    const trimmedAddress = address.trim();
    if (!trimmedPhone || !trimmedAddress) {
      setError('Phone number and address are required to sync yourself as an emergency contact.');
      return;
    }

    setSaving(true);
    try {
      await setDoc(doc(db, 'privateUsers', userId), {
        phone: trimmedPhone,
        address: trimmedAddress,
        profileLastModified: serverTimestamp(),
      }, { merge: true });
      onSaved?.({
        phone: trimmedPhone,
        address: trimmedAddress,
      });
    } catch (err) {
      setError(err?.message || 'Could not save your profile details.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={saving ? undefined : onClose}>
      <div
        className="modal-content"
        role="dialog"
        aria-modal="true"
        aria-label="Complete details for family emergency sync"
        style={{ maxWidth: 560 }}
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="modal-title">Complete Details For Family Sync</h2>
        <p className="text-muted" style={{ marginTop: 0, marginBottom: '1rem' }}>
          To use yourself as the emergency contact for child accounts, add your phone number and address.
        </p>

        {loading ? (
          <p>Loading profile details…</p>
        ) : (
          <form onSubmit={handleSave} className="flex-column" style={{ gap: '1rem' }}>
            <TextInput
              label="Phone Number"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              type="tel"
              autoComplete="tel"
              required
            />
            <TextInput
              label="Home Address"
              value={address}
              onChange={(event) => setAddress(event.target.value)}
              autoComplete="street-address"
              required
            />

            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <SubmitButton
                type="button"
                variant="ghost"
                label="Cancel"
                onClick={onClose}
                disabled={saving}
              />
              <SubmitButton
                type="submit"
                label={saving ? 'Saving…' : 'Save And Continue'}
                disabled={saving}
              />
            </div>

            <ErrorMessage message={error} />
          </form>
        )}
      </div>
    </div>
  );
}
